import { useState, useEffect } from "react";
import { perfumeAPI, fillingsAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Delete,
  Check,
  Droplets,
  Undo2,
  Clock,
  Trash2,
  X,
  Package,
} from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";

const FORMATS = ["30ml", "50ml", "100ml", "200ml", "500ml"];

// Natural sorting for perfume codes
const naturalSort = (a, b) => {
  const extractNum = (str) => {
    const match = str.match(/^(\d+)/);
    return match ? parseInt(match[1], 10) : 0;
  };
  const numA = extractNum(a.code);
  const numB = extractNum(b.code);
  if (numA !== numB) return numA - numB;
  return a.code.localeCompare(b.code);
};

const FillingRegister = () => {
  const [perfumes, setPerfumes] = useState([]);
  const [todayFillings, setTodayFillings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchCode, setSearchCode] = useState("");
  const [selectedPerfume, setSelectedPerfume] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState("100ml");
  const [action, setAction] = useState("fill"); // "fill" or "undo"
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startDate = today.toISOString();

      const [perfumesRes, fillingsRes] = await Promise.all([
        perfumeAPI.getAll(),
        fillingsAPI.getAll({ start_date: startDate })
      ]);
      setPerfumes(perfumesRes.data);
      setTodayFillings(fillingsRes.data);
    } catch (error) {
      toast.error("Errore caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  // Find matching perfumes based on search code
  const matchingPerfumes = searchCode
    ? perfumes
        .filter(p => p.code.toLowerCase().startsWith(searchCode.toLowerCase()) ||
                     p.code.toLowerCase().includes(searchCode.toLowerCase()))
        .sort(naturalSort)
        .slice(0, 6)
    : [];

  // Auto-select if only one result
  useEffect(() => {
    if (searchCode && matchingPerfumes.length === 1) {
      const timer = setTimeout(() => {
        setSelectedPerfume(matchingPerfumes[0]);
        setSearchCode("");
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [searchCode, matchingPerfumes]);

  const handleNumpadClick = (value) => {
    if (value === "DEL") {
      setSearchCode(prev => prev.slice(0, -1));
    } else if (value === "C") {
      setSearchCode("");
    } else if (value !== ".") {
      setSearchCode(prev => prev + value);
    }
  };

  const handleClear = () => {
    setSelectedPerfume(null);
    setSearchCode("");
  };

  const handleSelectPerfume = (perfume) => {
    setSelectedPerfume(perfume);
    setSearchCode("");
  };

  const handleSubmit = async () => {
    if (!selectedPerfume) {
      toast.error("Seleziona un profumo");
      return;
    }

    setSubmitting(true);
    try {
      await fillingsAPI.create({
        perfume_id: selectedPerfume.id,
        format: selectedFormat,
        action: action,
      });

      const actionText = action === "fill" ? "Riempimento" : "Annullamento";
      toast.success(`${actionText} registrato: ${selectedPerfume.code} - ${selectedFormat}`);
      
      loadData();
      handleClear();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore registrazione");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteFilling = async (fillingId) => {
    try {
      await fillingsAPI.delete(fillingId);
      toast.success("Registro eliminato");
      loadData();
    } catch (error) {
      toast.error("Errore eliminazione");
    }
  };

  // Group today's fillings by format
  const fillingsByFormat = todayFillings.reduce((acc, f) => {
    const key = f.format;
    if (!acc[key]) acc[key] = { fill: 0, undo: 0, items: [] };
    if (f.action === "fill") acc[key].fill++;
    else acc[key].undo++;
    acc[key].items.push(f);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#C9A227] animate-pulse">Caricamento...</div>
      </div>
    );
  }

  return (
    <div data-testid="filling-register-page" className="animate-fadeIn">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-2xl md:text-3xl font-serif text-gray-900 mb-1">Registro Riempimento</h1>
        <p className="text-gray-500 text-sm">Registra le bottiglie imbottigliate</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Column - Format Selection & Action */}
        <div className="space-y-3">
          {/* Format Selection */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-3 text-sm">FORMATO</h3>
            <div className="grid grid-cols-5 gap-1.5">
              {FORMATS.map((fmt) => (
                <button
                  key={fmt}
                  data-testid={`format-${fmt}`}
                  onClick={() => setSelectedFormat(fmt)}
                  className={`py-3 rounded-lg text-center font-bold transition-all text-sm ${
                    selectedFormat === fmt
                      ? "bg-[#C9A227] text-white shadow-md scale-105"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {fmt.replace("ml", "")}
                </button>
              ))}
            </div>
          </div>

          {/* Action Selection */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-3 text-sm">AZIONE</h3>
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="action-fill"
                onClick={() => setAction("fill")}
                className={`py-4 px-3 rounded-lg font-medium transition-all flex flex-col items-center gap-2 ${
                  action === "fill"
                    ? "bg-blue-500 text-white shadow-md"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <Droplets className="w-6 h-6" />
                <span className="text-sm">Riempimento</span>
                <span className="text-xs opacity-70">Decurta ML</span>
              </button>
              <button
                data-testid="action-undo"
                onClick={() => setAction("undo")}
                className={`py-4 px-3 rounded-lg font-medium transition-all flex flex-col items-center gap-2 ${
                  action === "undo"
                    ? "bg-orange-500 text-white shadow-md"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <Undo2 className="w-6 h-6" />
                <span className="text-sm">Annulla</span>
                <span className="text-xs opacity-70">Aggiungi ML</span>
              </button>
            </div>
          </div>

          {/* Selected Perfume Display */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-900 text-sm flex items-center gap-2">
                <Package className="w-4 h-4 text-[#C9A227]" />
                PROFUMO
              </h3>
              {selectedPerfume && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClear}
                  className="text-gray-400 hover:text-red-500 h-6 px-2"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>

            {selectedPerfume ? (
              <div className="p-3 bg-gradient-to-r from-blue-50 to-blue-100/50 rounded-lg border border-blue-200">
                <p className="text-blue-600 text-lg font-bold">{selectedPerfume.code}</p>
                <p className="text-gray-700 font-medium text-sm truncate">{selectedPerfume.name}</p>
                <p className="text-xs text-gray-500 mt-1">Stock: {selectedPerfume.quantity_ml.toFixed(0)} ml</p>
              </div>
            ) : searchCode ? (
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {matchingPerfumes.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => handleSelectPerfume(p)}
                    className="w-full p-2 text-left bg-gray-50 hover:bg-blue-50 rounded-lg border border-gray-100 hover:border-blue-200 transition-colors"
                  >
                    <span className="text-blue-600 font-bold mr-2">{p.code}</span>
                    <span className="text-gray-700 text-sm">{p.name}</span>
                  </button>
                ))}
                {matchingPerfumes.length === 0 && (
                  <p className="text-gray-400 text-center py-2 text-sm">Nessun risultato</p>
                )}
              </div>
            ) : (
              <p className="text-gray-400 text-sm text-center py-2">Digita il codice...</p>
            )}
          </div>
        </div>

        {/* Middle Column - Numpad */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          {/* Display */}
          <div className="mb-4">
            <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-2">
              Codice Profumo
            </p>
            <div className="bg-gray-100 rounded-xl p-3 text-right">
              <span className="text-3xl md:text-4xl font-bold tabular-nums text-gray-900">
                {searchCode || "..."}
              </span>
            </div>
          </div>

          {/* Numpad */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            {["7", "8", "9", "4", "5", "6", "1", "2", "3", "C", "0", "DEL"].map((key) => (
              <button
                key={key}
                data-testid={`numpad-${key}`}
                onClick={() => handleNumpadClick(key)}
                className={`h-14 md:h-16 rounded-xl text-xl md:text-2xl font-bold transition-all active:scale-95 ${
                  key === "DEL"
                    ? "bg-red-100 text-red-600 hover:bg-red-200"
                    : key === "C"
                    ? "bg-gray-200 text-gray-600 hover:bg-gray-300"
                    : "bg-gray-100 text-gray-900 hover:bg-gray-200 active:bg-gray-300"
                }`}
              >
                {key === "DEL" ? <Delete className="w-5 h-5 mx-auto" /> : key}
              </button>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              onClick={handleClear}
              data-testid="clear-filling"
              className="h-12 text-gray-600 border-gray-300 hover:bg-gray-100 rounded-xl font-semibold"
            >
              <X className="w-4 h-4 mr-1" />
              Annulla
            </Button>
            <Button
              onClick={handleSubmit}
              data-testid="submit-filling"
              disabled={!selectedPerfume || submitting}
              className={`h-12 text-white rounded-xl font-semibold shadow-lg disabled:opacity-50 ${
                action === "fill" 
                  ? "bg-blue-500 hover:bg-blue-600" 
                  : "bg-orange-500 hover:bg-orange-600"
              }`}
            >
              <Check className="w-4 h-4 mr-1" />
              {submitting ? "..." : action === "fill" ? "Registra" : "Annulla ML"}
            </Button>
          </div>
        </div>

        {/* Right Column - Today's Summary */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900 text-sm flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#C9A227]" />
              REGISTRO OGGI
            </h3>
            <Badge className="bg-[#C9A227]/10 text-[#C9A227] border-[#C9A227]/30">
              {todayFillings.length}
            </Badge>
          </div>

          {/* Summary by format */}
          {Object.keys(fillingsByFormat).length > 0 && (
            <div className="grid grid-cols-5 gap-1 mb-4">
              {FORMATS.map((fmt) => {
                const data = fillingsByFormat[fmt];
                return (
                  <div key={fmt} className="text-center p-2 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500">{fmt}</p>
                    <p className="font-bold text-gray-900">{data?.fill || 0}</p>
                    {data?.undo > 0 && (
                      <p className="text-xs text-orange-500">-{data.undo}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="space-y-2 max-h-[350px] overflow-y-auto">
            {todayFillings.length === 0 ? (
              <p className="text-gray-400 text-center py-8 text-sm">Nessun riempimento oggi</p>
            ) : (
              todayFillings.map((filling) => (
                <div
                  key={filling.id}
                  className={`flex items-center justify-between p-2 rounded-lg border group ${
                    filling.action === "fill" 
                      ? "bg-blue-50 border-blue-100" 
                      : "bg-orange-50 border-orange-100"
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`font-bold text-sm ${
                        filling.action === "fill" ? "text-blue-600" : "text-orange-600"
                      }`}>
                        {filling.perfume_code}
                      </span>
                      <Badge 
                        className={`text-[10px] px-1.5 py-0 ${
                          filling.action === "fill"
                            ? "bg-blue-100 text-blue-600 border-blue-200"
                            : "bg-orange-100 text-orange-600 border-orange-200"
                        }`}
                      >
                        {filling.format}
                      </Badge>
                      {filling.action === "undo" && (
                        <Undo2 className="w-3 h-3 text-orange-500" />
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate">{filling.perfume_name}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">
                      {format(new Date(filling.created_at), "HH:mm")}
                    </span>
                    <button
                      onClick={() => handleDeleteFilling(filling.id)}
                      className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-all"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FillingRegister;
